import { Eye, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';

import { PolicyTableRow } from '@/types/service/policy';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { ColumnDef } from '@tanstack/react-table';

export const createPolicyColumns = (
  onView: (policy: PolicyTableRow) => void,
  onEdit: (policy: PolicyTableRow) => void,
  onDelete: (policy: PolicyTableRow) => void,
): ColumnDef<PolicyTableRow>[] => [
  {
    accessorKey: 'name',
    header: 'Policy Name',
    cell: ({ row }) => <div className='font-medium'>{row.getValue('name')}</div>,
    enableSorting: true,
    enableGlobalFilter: true,
  },
  {
    id: 'created_by',
    header: 'Created By',
    cell: ({ row }) => {
      const policy = row.original;
      return (
        <div className='text-sm'>
          {policy.user_first_name} {policy.user_last_name}
        </div>
      );
    },
    enableGlobalFilter: true,
  },
  {
    accessorKey: 'created_at',
    header: 'Created At',
    cell: ({ row }) => (
      <div className='text-sm'>{new Date(row.getValue('created_at')).toLocaleString()}</div>
    ),
    enableSorting: true,
  },
  {
    accessorKey: 'updated_at',
    header: 'Updated At',
    cell: ({ row }) => (
      <div className='text-sm'>{new Date(row.getValue('updated_at')).toLocaleString()}</div>
    ),
    enableSorting: true,
  },
  {
    id: 'updated_by',
    header: 'Updated By',
    cell: ({ row }) => {
      const policy = row.original;
      return (
        <div className='text-sm'>
          {policy.updated_by_first_name} {policy.updated_by_last_name}
        </div>
      );
    },
    enableGlobalFilter: true,
  },
  {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => {
      const policy = row.original;
      const isUpdating = policy.isUpdating || false;
      const isDeleting = policy.isDeleting || false;
      const isDisabled = isUpdating || isDeleting;

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant='ghost' className='h-8 w-8 p-0' disabled={isDisabled}>
              <span className='sr-only'>Open menu</span>
              <MoreHorizontal className='h-4 w-4' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end'>
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onView(policy)} disabled={isDisabled}>
              <Eye className='mr-2 h-4 w-4' />
              View Policy
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onEdit(policy)} disabled={isDisabled}>
              <Pencil className='mr-2 h-4 w-4' />
              Edit Policy
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => onDelete(policy)}
              disabled={isDisabled}
              className='text-destructive focus:text-destructive'
            >
              <Trash2 className='mr-2 h-4 w-4' />
              Delete Policy
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];
